import { useMemo, useState } from 'react'
import { Button, Tooltip } from '@heroui/react'
import { ArrowLeft, ArrowRight, Save, Send } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'

import { appStore, useAppStore, isApplicationLocked } from '@/store'

const steps = [
	{ key: 'general', label: 'General Information', path: '/account/steps/general' },
	{ key: 'background', label: 'Your Background', path: '/account/steps/background' },
	{ key: 'program', label: 'Program Choice', path: '/account/steps/program' },
	{ key: 'aid', label: 'Financial Aid', path: '/account/steps/aid' },
] as const

const orderedPaths = [
	'/account/steps',
	'/account/steps/general',
	'/account/steps/background',
	'/account/steps/program',
	'/account/steps/aid',
] as const

export default function SidebarControls({
	className,
	isMobile = false,
}: {
	className?: string
	isMobile?: boolean
}) {
	const { user, dirty, saving, fileOperation } = useAppStore()
	const location = useLocation()
	const nav = useNavigate()

	const isSubmitted = isApplicationLocked(user?.application_process)
	const [submitting, setSubmitting] = useState(false)

	const currentIndex = useMemo(() => {
		const i = orderedPaths.findIndex((p) => location.pathname === p)
		return i >= 0 ? i : 0
	}, [location.pathname])

	// Block navigation if submitting or submitted
	const blockNav = isSubmitted || submitting
	const onPrev = () => {
		if (!blockNav && currentIndex > 0) nav(orderedPaths[currentIndex - 1])
	}
	const onNext = () => {
		if (!blockNav && currentIndex < orderedPaths.length - 1) nav(orderedPaths[currentIndex + 1])
	}

	// Allow the controls on any account steps sub-route (robust to query params or trailing slash)
	const pathname = location.pathname.replace(/\/+$/, '') // strip trailing slash
	const isStepsRoute = pathname.startsWith('/account/steps') && !pathname.endsWith('/done')

	if (isSubmitted || !isStepsRoute) return null

	const disabled = saving || fileOperation

	// Failures are reported to the user by appStore.save (toast); the edits stay
	// dirty so the user can simply try again, hence the swallowed rejections.
	const handleSave = async () => {
		await appStore.save({ source: 'manual' }).catch(() => {})
	}

	const handleSubmit = async () => {
		setSubmitting(true)
		try {
			// Only call save with submit flag; let backend handle status and process
			const fresh = await appStore.save({ submit: true, source: 'manual' })
			if (fresh?.application_process === 'Submitted') {
				appStore.sync(fresh)
				nav('/account/steps/done')
			}
		} catch {
			// toast already shown; application is still a Draft and can be resubmitted
		} finally {
			setSubmitting(false)
		}
	}

	// Submit button variants (single render with computed props)
	const renderSubmitButton = () => {
		const isSubmitted = user?.application_process === 'Submitted'
		const status = (user && (user.application_status as Record<string, unknown>)) || {}
		const keys = steps.map((s) => s.key)
		const completed = keys.filter((k) => Boolean((status as Record<string, unknown>)[k])).length
		const allComplete = completed === steps.length

		const base =
			'rounded-large mt-4 flex w-full items-center justify-center gap-2 font-semibold shadow-sm transition-all duration-300'

		if (!isSubmitted && !allComplete) {
			const incomplete = steps.filter((s) => !status[s.key]).map((s) => s.label)
			return (
				// The disabled Button swallows pointer events, so the tooltip anchors to a wrapper.
				<Tooltip content={`Finish these steps first: ${incomplete.join(', ')}`} placement="top">
					<div>
						<Button
							className={`${base} text-success-800 cursor-not-allowed border-[#0E793C] bg-[#0E793C]`}
							color="success"
							isDisabled
							endContent={<Send className="size-4 text-white" />}
							aria-disabled="true"
						>
							Submit
						</Button>
					</div>
				</Tooltip>
			)
		}

		if (!isSubmitted && allComplete) {
			return (
				<Button
					className={`${base} bg-green-600 text-white`}
					color="success"
					isDisabled={disabled || submitting}
					onPress={handleSubmit}
					endContent={
						submitting ? <span className="loader mr-2" /> : <Send className="size-4 text-white" />
					}
				>
					{submitting ? 'Submitting...' : disabled ? 'Saving...' : 'Submit'}
				</Button>
			)
		}

		return (
			<Button
				className={`${base} bg-default-200 text-default-500 cursor-not-allowed`}
				color="success"
				isDisabled
				endContent={<Send className="text-default-400 size-4" />}
				aria-disabled="true"
			>
				Submitted
			</Button>
		)
	}

	return (
		<div className={className}>
			<div className="flex justify-between gap-3">
				<Button
					color="default"
					onPress={onPrev}
					isDisabled={currentIndex <= 0 || blockNav}
					className="w-full transition-all duration-500"
					startContent={<ArrowLeft className="size-4" />}
					size={isMobile ? 'md' : 'sm'}
				>
					Prev
				</Button>

				<Tooltip
					isDisabled={dirty}
					content="No changes to save"
					placement="top"
					className="capitalize"
				>
					<Button
						color="primary"
						variant="solid"
						className={`w-full transition-all duration-500 ${saving || fileOperation ? 'animate-pulse' : ''}`}
						onPress={handleSave}
						isDisabled={saving || fileOperation || isSubmitted}
						endContent={!saving ? <Save className="size-4" /> : undefined}
						size={isMobile ? 'md' : 'sm'}
					>
						{saving || fileOperation ? 'Saving...' : 'Save'}
					</Button>
				</Tooltip>

				<Button
					color="default"
					onPress={onNext}
					isDisabled={currentIndex >= orderedPaths.length - 1 || blockNav}
					className="w-full transition-all duration-500"
					endContent={<ArrowRight className="size-4" />}
					size={isMobile ? 'md' : 'sm'}
				>
					Next
				</Button>
			</div>

			{renderSubmitButton()}
		</div>
	)
}
